/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * One portal-wide keyboard focus ring.
 *
 * WHY THIS EXISTS AT ALL: MUI v4's ButtonBase sets `outline: 0` and never
 * restored a ring, so every button in this app was focusable with no visible
 * indicator -- measured on the Claude Usage rail, where adding `Mui-focusVisible`
 * to a toggle, an icon button and a text button changed nothing at all. Outlined
 * inputs were fine (1px -> 2px on `Mui-focused`); it is only buttons that were
 * invisible. That is WCAG 2.4.7 (AA) and it contradicts PRODUCT.md's own
 * commitment that every control is "operable by keyboard, with visible focus".
 *
 * WHY GLOBAL CSS RATHER THAN A THEME OVERRIDE: this app renders MUI v4 and v5
 * side by side (the panel's controls are @material-ui/core; the shell is @mui).
 * A `components.MuiButtonBase` override reaches only the v5 half. A plain
 * `:focus-visible` selector is version-agnostic and fixes both in one place.
 *
 * WHY primary.main: the Claude Usage table already rings its sort and collapse
 * buttons with `primary.main` at 2px, so this matches an existing convention
 * rather than introducing a second focus colour. It is theme-aware, and measured
 * against every surface a ring can land on it clears the 3:1 non-text minimum
 * (WCAG 1.4.11) in both themes: #B5371F is 4.10:1 on the sand page and 5.09:1 on
 * cream cards; the dark theme's coral is 6.00:1 on ink, 5.22:1 on inkPaper and
 * 6.51:1 on inkNav.
 *
 * Two colours were rejected on measurement, not taste. accentDeep (#E04A36) is
 * 2.78:1 on the sand page -- a brand ring would have been quietly inaccessible on
 * half the portal. text.primary passes everywhere with 11-15:1 but reads as a
 * border rather than a focus cue and would have been a second convention.
 *
 * SCOPED TO BUTTONS AND LINKS on purpose: inputs already indicate focus through
 * their notched outline, and doubling a ring on top of that reads as an error
 * state. `:focus-visible` is what keeps this off mouse users.
 */
import React from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useTheme } from '@mui/material/styles';

export const FocusVisibleStyles = () => {
  const theme = useTheme();
  // `button:focus-visible` is specificity (0,1,1) and beats MUI v4's
  // `.MuiButtonBase-root { outline: 0 }` at (0,1,0) without !important, whatever
  // order the stylesheets happen to be injected in.
  const ring = {
    outline: `2px solid ${theme.palette.primary.main}`,
    // Offset so the ring reads as a ring rather than thickening the control's
    // own border, which is what made the segmented toggle group ambiguous.
    outlineOffset: 2,
  };
  return (
    <GlobalStyles
      styles={{
        'button:focus-visible': ring,
        'a:focus-visible': ring,
        '[role="button"]:focus-visible': ring,
        '[role="tab"]:focus-visible': ring,
        '[role="option"]:focus-visible': ring,
        'summary:focus-visible': ring,
        // A focused control inside a scroll container must not be clipped by it.
        '*:focus-visible': { scrollMarginBlock: theme.spacing(2) },
      }}
    />
  );
};
